import React from 'react'
import _ from 'lodash'

import OrderActions from '../../actions/OrderActions.js'
import t from '../../helpers/Languages.js'
import naturalSortBy from '../../utils/naturalSortBy.js'

import {Alert, IconButton, S1, Row, Card, P} from '../UI/index.js'
import {formSpacing, FormRow, FormStack, Input, Select, TextArea, CurrencyInput, Toggle, DatePicker, MetricInput} from '../UI/forms/index.js'
import {Customer, Order, Reseller} from '../../../types/types.js'
import {formatPrice, parsePrice} from '../../../utils/price.js'
import {priceTableHasFixedLocation} from '../../../utils/priceTableLocation.js'
import {getColliTypeName} from '../../../utils/getColliTypeName.js'
import {getColliTypesSummary, getIncludedColli} from '../../../utils/colliTypesIncluded.js'
import {showColliBarcodeSpecification} from '../../../utils/showColliBarcodeSpecification.js'

interface AdministrationProps {
    order: Order
    reseller: Reseller
    customer?: Customer
    onUpdateOrder: (order: Order) => void
}

interface AdministrationState {
    order: Order
    dontInvoice: boolean
    invoiceDate: string
    priceTableId: string
    distance: number
    customDistance: boolean
    feeIds: string[]
    reference: string
    price: string
    priceExplanation: Order['priceExplanation']
    fees: Order['fees']
    edit: boolean
    expanded: boolean
    priceLoading: boolean
    loading: boolean
    error: string
    message: string
}

class Administration extends React.PureComponent<AdministrationProps, AdministrationState> {
    initialState: AdministrationState

    constructor(props) {
        super(props)

        this.initialState = {
            order: null,
            dontInvoice: true,
            invoiceDate: null,
            priceTableId: '',
            distance: 0,
            customDistance: false,
            feeIds: [],
            reference: '',
            price: '0,00',
            priceExplanation: {},
            fees: [],
            edit: false,
            expanded: false,
            priceLoading: false,
            loading: false,
            error: '',
            message: ''
        }

        this.state = structuredClone(this.initialState)
    }

    componentDidMount() {
        this.setState({
            order: structuredClone(this.props.order),
            ...this.getEditableValues(this.props.order),
            priceExplanation: this.props.order.priceExplanation
        })
    }

    getEditableValues(order: Order) {
        return {
            dontInvoice: !!order.dontInvoice,
            invoiceDate: order.invoiceDate,
            priceTableId: order.priceTableId,
            distance: order.distance,
            customDistance: order.customDistance,
            feeIds: [...(order.feeIds || [])],
            reference: order.reference,
            price: order.price,
            fees: [...(order.fees || [])]
        }
    }

    // Whether the administration is being edited and differs from the order, used to warn before closing the order
    hasChanges() {
        const {edit} = this.state
        const savedValues = this.getEditableValues(this.props.order)

        return edit && !_.isEqual(_.pick(this.state, Object.keys(savedValues)), savedValues)
    }

    saveChanges(callback: (err?: string) => void) {
        this.save(callback)
    }

    componentDidUpdate(prevProps) {
        const {edit} = this.state
        if (!edit) {
            const {order} = this.props
            if (!_.isEqual(order, prevProps.order)) {
                this.setState({
                    order: structuredClone(order),
                    ...this.getEditableValues(order)
                })
            }
        }
    }

    onChangePricetableId(event) {
        const {reseller} = this.props
        const priceTable = _.find(reseller.settings.orders.prices, {id: event.target.value})

        this.setState({priceTableId: priceTable?.id || '', feeIds: [], price: '0,00', priceExplanation: undefined}, () => {
            if (priceTable?.id) {
                this.calculateDistanceAndPrice()
            }
        })
    }

    onChangeFees(event) {
        this.setState({feeIds: [...event.target.values]}, () => {
            this.calculateDistanceAndPrice()
        })
    }

    onChangeDistance(event) {
        this.setState({priceLoading: true, error: ''})

        this.setState({distance: event.target.value}, () => {
            const {order, priceTableId, feeIds, customDistance} = this.state

            order.priceTableId = priceTableId
            order.feeIds = feeIds
            order.distance = event.target.value
            order.customDistance = customDistance

            OrderActions.getPrice(order, (err, price, _, priceExplanation) => {
                if (!err) {
                    this.setState({priceLoading: false, price, priceExplanation})
                } else {
                    this.setState({priceLoading: false, error: err})
                }
            })
        })
    }

    calculateDistanceAndPrice() {
        const {order, priceTableId, feeIds, distance, customDistance} = this.state

        this.setState({priceLoading: true, error: ''})

        order.priceTableId = priceTableId
        order.feeIds = feeIds
        if (distance) {
            order.distance = distance
        }
        order.customDistance = customDistance

        OrderActions.getPrice(order, (err, price, distance, priceExplanation) => {
            if (!err) {
                this.setState({priceLoading: false, price, distance, priceExplanation})
            } else {
                this.setState({priceLoading: false, error: err})
            }
        })
    }

    getPickupAndDeliveryOptionsSubtotal() {
        const {priceExplanation} = this.state

        if (priceExplanation?.pickupAndDeliveryOptions) {
            return formatPrice(priceExplanation.pickupAndDeliveryOptions.reduce((acc, option) => acc + parsePrice(option.subtotal), 0))
        }

        return '0,00'
    }

    save(callback?: (err?: string) => void) {
        const {dontInvoice, invoiceDate, priceTableId, distance, customDistance, feeIds, reference, price, priceExplanation, fees} = this.state
        const {order} = this.props

        this.setState({loading: true, error: ''})

        OrderActions.editAdministration(order._id, dontInvoice, invoiceDate, priceTableId, distance, customDistance, feeIds, reference, price === 'priceScale' ? price : formatPrice(price), priceExplanation, fees, (err, order) => {
            if (err) {
                this.setState({loading: false, error: err})
            } else {
                this.setState({loading: false, edit: false, error: ''})

                this.props.onUpdateOrder(order)
            }

            callback?.(err)
        })
    }

    render() {
        const {order, dontInvoice, invoiceDate, priceTableId, distance, customDistance, feeIds, reference, price, priceExplanation, expanded, edit, priceLoading, error} = this.state
        const {reseller, customer} = this.props

        const priceTable = _.find(reseller.settings.orders.prices, {id: priceTableId})

        return (
            <div style={{marginBottom: 24, width: '100%'}}>
                <Row style={{alignItems: 'center', minHeight: 36}}>
                    <Row style={{cursor: 'pointer'}} onClick={() => this.setState({expanded: !expanded})}>
                        <S1>Administratie</S1>
                        <i style={{marginLeft: 6}}className={expanded ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'} />
                    </Row>

                    <div style={{flex: 1}} />

                    {!edit && expanded &&
                        <IconButton
                            icon='mdi mdi-pencil'
                            onClick={() => this.setState({edit: !edit})}
                        />
                    }

                    {edit &&
                        <IconButton
                            icon='mdi mdi-check'
                            onClick={() => this.save()}
                        />
                    }
                </Row>

                {expanded &&
                    <div style={{marginBottom: 12, width: '100%', padding: 6}}>
                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <FormRow>
                                <Toggle
                                    label='Rit factureren'
                                    checked={!dontInvoice}
                                    onChange={(event) => this.setState({dontInvoice: !event.target.checked})}
                                    disabled={!edit || order.invoiced}
                                />

                                <DatePicker
                                    label='Factuurdatum'
                                    value={invoiceDate}
                                    onChange={(event) => {
                                        if ('target' in event) {
                                            this.setState({invoiceDate: event.target.value})
                                        }
                                    }}
                                    readOnly={!edit}
                                    disabled={dontInvoice || order.invoiced}
                                />
                            </FormRow>

                            <FormRow>
                                <Select
                                    label={t('priceTable')}
                                    allowEmpty
                                    value={priceTableId}
                                    onChange={this.onChangePricetableId.bind(this)}
                                    readOnly={!edit}
                                    disabled={dontInvoice}
                                    loading={priceLoading}
                                >
                                    {naturalSortBy(reseller.settings.orders.prices || [], 'name').map((priceTable) => {
                                        let disabled = false

                                        if (priceTable?.distanceFeeType === 'distance' && !priceTableHasFixedLocation(priceTable) && order?.addresses.length === 1) {
                                            disabled = true
                                        }
                                        if (priceTable.distanceFeeType === 'zones' && order?.addresses.length > 2) {
                                            disabled = true
                                        }

                                        return (
                                            <option key={priceTable.id} value={priceTable.id} disabled={disabled}>{priceTable.name}</option>
                                        )
                                    })}
                                </Select>

                                <Select
                                    style={{flex: 1}}
                                    label='Toeslag'
                                    values={feeIds}
                                    options={(priceTable?.fees || []).filter((fee) => !fee.auto).map((fee) => {
                                        return {value: fee.id, title: fee.description}
                                    })}
                                    onChange={this.onChangeFees.bind(this)}
                                    disabled={!priceTableId || !priceTable || dontInvoice}
                                    readOnly={!edit}
                                />
                            </FormRow>

                            <FormRow>
                                <TextArea
                                    label='Referentie'
                                    value={reference}
                                    onChange={(event) => this.setState({reference: event.target.value})}
                                    disabled={dontInvoice}
                                    readOnly={!edit}
                                />

                                <FormRow style={{flex: 1}}>
                                    {priceTableId && priceTable?.distanceFeeType === 'distance' && typeof distance === 'number' &&
                                        <>
                                            {customDistance ?
                                                <>
                                                    <MetricInput
                                                        label='Afstand'
                                                        value={distance}
                                                        onChange={this.onChangeDistance.bind(this)}
                                                        precision={2}
                                                        append='km'
                                                        disabled={dontInvoice}
                                                        readOnly={!edit}
                                                    />
                                                    {edit &&
                                                        <IconButton
                                                            icon='mdi mdi-refresh'
                                                            onClick={() => {
                                                                this.setState({customDistance: false}, () => {
                                                                    this.calculateDistanceAndPrice()
                                                                })
                                                            }}
                                                            tooltip='Afstand herberekenen'
                                                            disabled={dontInvoice}
                                                        />
                                                    }
                                                </> :
                                                <>
                                                    <Input
                                                        label='Afstand'
                                                        value={`${distance.toString().replace('.', ',')} km`}
                                                        readOnly
                                                    />
                                                    {edit &&
                                                        <IconButton
                                                            icon='mdi mdi-pencil'
                                                            onClick={() => {
                                                                this.setState({customDistance: true})
                                                            }}
                                                        />
                                                    }
                                                </>
                                            }
                                        </>

                                    }

                                    <CurrencyInput
                                        label='Prijs'
                                        value={price === 'priceScale' ? (order.priceScaleRange && !edit ? `${order.priceScaleRange.min} - ${order.priceScaleRange.max}` : 'n.t.b.') : price}
                                        onChange={(event) => this.setState({price: event.target.value})}
                                        disabled={dontInvoice}
                                        readOnly={!!priceTableId || !edit}
                                    />
                                </FormRow>
                            </FormRow>
                        </FormStack>

                        {priceExplanation && !_.isEmpty(priceExplanation) &&
                            <Card>
                                <S1 style={{marginBottom: 12}}>{t('priceExplanationTitle')}</S1>

                                {priceExplanation.product?.name && priceExplanation.product?.price &&
                                    <Row style={{width: '50%', marginBottom: 12}}>
                                        <P style={{flex: 1}}>{priceExplanation.product.name}</P>
                                        <P>€ {typeof priceExplanation.product.price === 'string' ? priceExplanation.product.price : `${priceExplanation.product.price.min} - € ${priceExplanation.product.price.max}`}</P>
                                    </Row>
                                }

                                {priceExplanation.distanceZone &&
                                    <>
                                        <Row style={{width: '50%', marginBottom: 12}}>
                                            <P style={{flex: 1}}>{t('priceExplanationZoneDistance', priceExplanation.distanceZone.measuredBy)}{priceExplanation.product?.zoneName ? ` (${priceExplanation.product.zoneName})` : ''}</P>
                                            <P>{(priceExplanation.distanceZone.distance / 1000).toFixed(1).replace('.', ',')} km</P>
                                        </Row>

                                        {priceExplanation.distanceZone.roadDistanceUnavailable &&
                                            <Alert variant='warning' style={{width: '50%', marginBottom: 12}}>{t('priceExplanationRoadDistanceUnavailable')}</Alert>
                                        }
                                    </>
                                }

                                {priceExplanation.overruledPrice && priceExplanation.overruledPrice !== '0,00' &&
                                    <Row style={{width: '50%', marginBottom: 12}}>
                                        <P style={{flex: 1}}>{t('price')}</P>
                                        <P>€ {priceExplanation.overruledPrice}</P>
                                    </Row>
                                }

                                {priceExplanation.minimumRate && priceExplanation.minimumRate !== '0,00' ?
                                    <Row style={{width: '50%'}}>
                                        <P style={{flex: 1}}>{t('priceExplanationStartRate')}</P>
                                        <P>€ {priceExplanation.minimumRate}</P>
                                    </Row> :
                                    <>
                                        {priceExplanation.startRate && priceExplanation.startRate !== '0,00' &&
                                            <Row style={{width: '50%', marginBottom: 12}}>
                                                <P style={{flex: 1}}>{t('priceExplanationStartRate')}</P>
                                                <P>€ {priceExplanation.startRate}</P>
                                            </Row>
                                        }

                                        {priceExplanation.feeExtraStop && priceExplanation.feeExtraStop.subtotal !== '0,00' &&
                                            <Row style={{marginBottom: 12}}>
                                                <Row style={{width: '50%'}}>
                                                    <P style={{flex: 1}}>{t('priceExplanationStops', priceExplanation.feeExtraStop.nrOfStops)}</P>
                                                    <P>€ {priceExplanation.feeExtraStop.subtotal}</P>
                                                </Row>
                                            </Row>
                                        }

                                        {priceExplanation.feeForPickupAddress && priceExplanation.feeForPickupAddress.subtotal !== '0,00' &&
                                            <Row style={{marginBottom: 12}}>
                                                <Row style={{width: '50%'}}>
                                                    <P style={{flex: 1}}>{t('priceExplanationPickupAddresses', priceExplanation.feeForPickupAddress.nrOfAddresses)}</P>
                                                    <P>€ {priceExplanation.feeForPickupAddress.subtotal}</P>
                                                </Row>
                                            </Row>
                                        }

                                        {priceExplanation.feeForDeliveryAddress && priceExplanation.feeForDeliveryAddress.subtotal !== '0,00' &&
                                            <Row style={{marginBottom: 12}}>
                                                <Row style={{width: '50%'}}>
                                                    <P style={{flex: 1}}>{t('priceExplanationDeliveryAddresses', priceExplanation.feeForDeliveryAddress.nrOfAddresses)}</P>
                                                    <P>€ {priceExplanation.feeForDeliveryAddress.subtotal}</P>
                                                </Row>
                                            </Row>
                                        }

                                        {priceExplanation.feeForExtraCollo && priceExplanation.feeForExtraCollo.subtotal !== '0,00' &&
                                            <Row style={{marginBottom: 12}}>
                                                <Row style={{width: '50%'}}>
                                                    <P style={{flex: 1}}>{t('priceExplanationColli', priceExplanation.feeForExtraCollo.nrOfColli)}</P>
                                                    <P>€ {priceExplanation.feeForExtraCollo.subtotal}</P>
                                                </Row>
                                            </Row>
                                        }
                                        {priceExplanation.colliTypes?.length > 0 &&
                                            <Row>
                                                <P style={{flex: 1}}>{t('priceExplanationColliTotal', getColliTypesSummary(priceExplanation.colliTypes))}</P>
                                                <P style={{marginRight: 32}}>€ {priceExplanation.colliTypes.reduce((sum, colliType) => sum + parseFloat(colliType.subtotal.replace(',', '.')), 0).toFixed(2).replace('.', ',')}</P>
                                            </Row>
                                        }
                                        {showColliBarcodeSpecification(reseller, customer) && order.colli?.length > 0 &&
                                            order.colli.map((collo, index, colli) => {
                                                const colliTypeName = getColliTypeName(collo.type, reseller)
                                                const included = getIncludedColli(colli.map((collo) => getColliTypeName(collo.type, reseller)), priceExplanation.colliTypes)[index]
                                                const colliTypeFee = priceExplanation.colliTypes?.find((type) => type.type === colliTypeName)
                                                const colloDescription = [collo.barcode, colliTypeName].filter(Boolean).join(' - ')

                                                // A collo without barcode and type would only render a stray ' - '
                                                if (!colloDescription) return null

                                                return (
                                                    <Row key={`colli-barcode-${index}`}>
                                                        <P style={{flex: 1, marginLeft: 20}}>{colloDescription}</P>
                                                        {(included || colliTypeFee?.fee) &&
                                                            <P style={{marginRight: 32 + 20}}>{included ? t('priceExplanationColloIncluded') : `€ ${colliTypeFee.fee}`}</P>
                                                        }
                                                    </Row>
                                                )
                                            })
                                        }
                                        {priceExplanation.pickupAndDeliveryOptions?.length > 0 && this.getPickupAndDeliveryOptionsSubtotal() !== '0,00' ?
                                            <Row style={{marginBottom: 12}}>
                                                <Row style={{width: '50%'}}>
                                                    <P style={{flex: 1}}>{t('priceExplanationPickupAndDeliveryOptions', priceExplanation.pickupAndDeliveryOptions.length)}</P>
                                                    <P>€ {this.getPickupAndDeliveryOptionsSubtotal()}</P>
                                                </Row>
                                            </Row> :
                                            null
                                        }
                                        {priceExplanation.distanceFee &&
                                            <Row style={{marginBottom: 12}}>
                                                <Row style={{width: '50%'}}>
                                                    <P style={{flex: 1}}>{t('priceExplanationDistanceFee', {distance: (priceExplanation.distanceFee.distance - (priceExplanation.distanceFee.distanceIncluded || 0)).toFixed(2).replace('.', ','), fee: priceExplanation.distanceFee.fee})}</P>
                                                    <P>€ {priceExplanation.distanceFee.subtotal}</P>
                                                </Row>
                                            </Row>
                                        }

                                        {priceExplanation.zoneFees &&
                                            <Row style={{marginBottom: 12}}>
                                                <Row style={{width: '50%'}}>
                                                    <P style={{flex: 1}}>{t('deliveryZone')} {priceExplanation.zoneFees.zones[0]}{priceExplanation.zoneFees.zones?.[1] ? ` - ${priceExplanation.zoneFees.zones[1]}` : ''}</P>
                                                    <P>€ {priceExplanation.zoneFees.subtotal}</P>
                                                </Row>
                                            </Row>
                                        }

                                        {priceExplanation.fuelFee?.subtotal &&
                                            <Row style={{marginBottom: 12}}>
                                                <Row style={{width: '50%'}}>
                                                    <P style={{flex: 1}}>{t('priceExplanationFuelFee', priceExplanation.fuelFee.percentage)}</P>
                                                    <P>€ {priceExplanation.fuelFee.subtotal}</P>
                                                </Row>
                                            </Row>
                                        }

                                        {priceExplanation.fees?.length > 0 ?
                                            priceExplanation.fees.map((fee) => (
                                                <Row key={fee.name} style={{width: '50%'}}>
                                                    <P style={{flex: 1}}>{fee.name}</P>
                                                    <P>€ {fee.fee || fee.price}</P>
                                                </Row>
                                            )) :
                                            null
                                        }
                                        {priceExplanation.rounding &&
                                            <Row style={{width: '50%'}}>
                                                <P style={{flex: 1, fontWeight: 700}}>{t('priceExplanationRounding')} {[
                                                    {value: 1, title: t('priceExplanationRoundingWholeEuros')},
                                                    {value: 2.5, title: t('priceExplanationRoundingMultipleOf', '€2,50')},
                                                    {value: 5, title: t('priceExplanationRoundingMultipleOf', '€5,00')},
                                                    {value: 10, title: t('priceExplanationRoundingMultipleOf', '€10,00')},
                                                    {value: 5.05, title: t('priceExplanationRoundingMultipleOf', '€5,00 - €0,05')},
                                                    {value: 10.05, title: t('priceExplanationRoundingMultipleOf', '€10,00 - €0,05')}
                                                ].find((r) => r.value === priceExplanation.rounding.rounding)?.title}: €{priceExplanation.rounding.before}
                                                </P>
                                                <P style={{fontWeight: 700}}>€ {priceExplanation.rounding.subtotal}</P>
                                            </Row>
                                        }
                                    </>
                                }
                            </Card>
                        }

                        {error &&
                            <Alert style={{marginBottom: 6}} variant='danger'>{error}</Alert>
                        }
                    </div>
                }
            </div>

        )
    }
}

export default Administration
